import { icon } from "../iconsKit";
import { BuilderPick } from "./builderPick";

/**
 * Tab-bar badge colour rule (hub redesign, DECISION.md graft 2): red means "there is something you can do now",
 * blue means "for your information" (a timer running, a job just finished). A badge never shows on something the
 * player cannot act on or has not unlocked.
 */
export type BadgeTone = "alert" | "info";

/** `glyph` draws a symbol instead of `text`: "up" is the green up-arrow that also marks the ready tower's tile. */
export interface Badge { readonly tone: BadgeTone; readonly text: string; readonly timer?: boolean; readonly glyph?: "up" }

/** How long (seconds) the Towers tab shows ✓ after a job finishes while the hub is open. */
export const DONE_BADGE_SECONDS = 5;

/** Short ETA for a badge (fits ~40 px): 45s, 12m, 3h. */
export function fmtBadgeEta(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  const MIN = 60, HOUR = 3600;
  return s < MIN ? `${s}s` : s < HOUR ? `${Math.ceil(s / MIN)}m` : `${Math.floor(s / HOUR)}h`;
}

/**
 * Towers: NEW when a tower was unlocked and the player hasn't opened the page since (rare, and cleared by one look, so
 * it goes first); else the green up-arrow when a builder is free and an upgrade is affordable that the player has not
 * looked at yet (`upgradeSeen`, upgradeNudge.ts: opening the page clears it, and the page points at that upgrade); else
 * a blue timer while a job runs; else ✓ for a moment after one finished.
 */
export function towersBadge(pick: BuilderPick, justFinished: boolean, newTowers = 0, upgradeSeen = false): Badge | null {
  if (newTowers > 0) return { tone: "alert", text: "NEW" };
  if (pick.kind === "idle" && pick.actionable && !upgradeSeen) return UPGRADE_BADGE;
  if (pick.kind === "busy") return { tone: "info", text: fmtBadgeEta(pick.remaining), timer: true };
  if (justFinished) return { tone: "info", text: "✓" };
  return null;
}

/**
 * Gear: NEW while chest gear has come in since the player last opened the Gear tab (the save's `gearSeenUpTo`; the
 * Armoury marks it seen as it opens, and shows NEW on each of those items). M30 #15/#25: it used to count what
 * Equip best would put on, which went with Equip best.
 */
export function gearBadge(newItems: number, unlocked: boolean): Badge | null {
  return unlocked && newItems > 0 ? { tone: "alert", text: "NEW" } : null;
}

/**
 * Chests: a red count of what can be taken now (finished orders, filled Supply and weekly chests, today's check-in);
 * else, while the day's orders are under way, a blue "done/total" (e.g. 1/3) so the Orders on the page are findable.
 */
export function chestsBadge(claimable: number, unlocked: boolean, orders: { done: number; total: number } | null = null): Badge | null {
  if (!unlocked) return null;
  if (claimable > 0) return { tone: "alert", text: String(claimable) };
  return orders && orders.total > 0 && orders.done < orders.total ? { tone: "info", text: `${orders.done}/${orders.total}` } : null;
}

/** More: a red count of running events the player hasn't opened yet. */
export function moreBadge(unseenEvents: number): Badge | null {
  return unseenEvents > 0 ? { tone: "alert", text: String(unseenEvents) } : null;
}

/** The Towers tab's "upgrade ready" badge. */
export const UPGRADE_BADGE: Badge = { tone: "alert", text: "", glyph: "up" };

/** The up-arrow drawn in a badge and on a ready tower's tile (towersTab.ts), so the two read as one thing. */
export function upArrowSvg(size: number): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true"><path d="M12 4 L20 13 H15 V20 H9 V13 H4 Z" fill="#fff" stroke="#1e5a14" stroke-width="1.6" stroke-linejoin="round"/></svg>`;
}

export function badgeHtml(b: Badge | null): string {
  if (!b) return "";
  if (b.glyph === "up") return `<span class="badge up" role="img" aria-label="Upgrade ready">${upArrowSvg(14)}</span>`;
  return `<span class="badge ${b.tone}${b.timer ? " timer" : ""}">${b.timer ? icon("clock", 11) : ""}${b.text}</span>`;
}
